import type { IBookingDetail } from '../types/schema'
import { decodeEntities } from './decodeEntities'
import { durationToMinutes } from './timeSlots'

/**
 * Add-to-calendar, built entirely from the booking response — no extra API call.
 * Times are floating local values (no `Z`, no `TZID`): a spa appointment is local to the branch.
 */

interface CalendarEvent {
  title: string
  start: Date
  end: Date
  location: string
  description: string
}

function buildEvent(detail: IBookingDetail, title: string): CalendarEvent | null {
  const start = new Date(`${detail.appt_date.slice(0, 10)}T${detail.appt_time.slice(0, 8)}`)
  if (Number.isNaN(start.valueOf())) return null

  const minutes = durationToMinutes(detail.total_hours) || 120
  const end = new Date(start.getTime() + minutes * 60_000)

  const product = decodeEntities(detail.gift_used?.[0]?.product ?? '')
  const description = [
    `Booking ID: ${detail.bpds_id}`,
    product && `Treatment: ${product}`,
    detail.branch_address && `Address: ${detail.branch_address}`,
    // An eGift arrives by email — there is no printed original to bring.
    'Please bring your booking ID and your eGift (on your phone or printed).'
  ]
    .filter(Boolean)
    .join('\n')

  return {
    title,
    start,
    end,
    location: detail.branch_address || detail.branch_name,
    description
  }
}

/** YYYYMMDDTHHMMSS — floating local time. */
function stamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')

  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  )
}

/** YYYYMMDDTHHMMSSZ — unlike DTSTART, DTSTAMP must be UTC (RFC 5545 §3.8.7.2). */
function stampUTC(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
}

/** RFC 5545 escaping: backslash, semicolon, comma, and newline all carry meaning. */
function escapeICS(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

export function buildGoogleCalendarUrl(detail: IBookingDetail, title: string): string | null {
  const event = buildEvent(detail, title)
  if (!event) return null

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${stamp(event.start)}/${stamp(event.end)}`,
    details: event.description,
    location: event.location
  })

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/** Triggers an .ics download. Client-only. Returns false when the event could not be built. */
export function downloadICS(detail: IBookingDetail, title: string): boolean {
  const event = buildEvent(detail, title)
  if (!event || typeof document === 'undefined') return false

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Oasis Spa//Redeem//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:oasis-redeem-${detail.bpds_id}@oasisspa.com`,
    `DTSTAMP:${stampUTC(new Date())}`,
    `DTSTART:${stamp(event.start)}`,
    `DTEND:${stamp(event.end)}`,
    `SUMMARY:${escapeICS(event.title)}`,
    `DESCRIPTION:${escapeICS(event.description)}`,
    `LOCATION:${escapeICS(event.location)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ]

  // CRLF is required by RFC 5545; Outlook in particular rejects bare LF.
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `oasis-spa-${detail.bpds_id}.ics`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)

  return true
}
